import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import classnames from 'classnames'

import Form from '../../../components/Form'
import Field from '../../../components/Form/Field'
import PhoneInput from '../../../components/Form/PhoneInput'
import FieldLabelWrapper from '../../../features/components/Form/FieldLabelWrapper'

import { formatOptionsList } from '../../../features/formatters'

import { choicesCountriesSelector, choicesCountriesWasLoadedSelector } from '../../../modules/selectors/choices'
import {
  getCurrentUserSelector,
  updateCurrentUserErrorSelector,
  updateCurrentUserLoadingSelector,
  updateCurrentUserWasUpdatedSelector
} from '../../../modules/selectors/app'
import { getChoicesCountries } from '../../../modules/actions/choices'
import { clearUpdateCurrentUserProfile, updateCurrentUserProfile } from '../../../modules/actions/app'

import { getInitialValues, validationSchema, FIRST_NAME, LAST_NAME, COUNTRY, PHONE_NUMBER } from './fields'

import useStyles from './styles'
import useCommonStyles from '../../../styles/common/listPage'

function EditProfile() {
  const commonClasses = useCommonStyles()
  const classes = useStyles()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const user = useSelector(getCurrentUserSelector)
  const countries = useSelector(choicesCountriesSelector)
  const countriesWasLoaded = useSelector(choicesCountriesWasLoadedSelector)
  const userWasUpdated = useSelector(updateCurrentUserWasUpdatedSelector)

  const { email, id } = user

  const formattedCountries = formatOptionsList({
    list: countries,
    valueName: 'id',
    labelName: 'name'
  })

  const onSubmit = values => {
    // we need to add + sign before phone number to match BE requirements
    dispatch(
      updateCurrentUserProfile(id, {
        ...values,
        // phone number is optional
        // allow to remove phone with sending ""
        phone_number: !!values[PHONE_NUMBER] ? `+${values[PHONE_NUMBER]}` : ''
      })
    )
  }

  const formik = useFormik({
    initialValues: getInitialValues(user),
    validationSchema,
    enableReinitialize: true,
    onSubmit
  })

  const { values } = formik

  const userCountry = values[COUNTRY]
  const userCountryIso2 = formattedCountries.find(item => item.id === userCountry)?.iso2?.toLowerCase()

  useEffect(() => {
    if (!countriesWasLoaded) {
      dispatch(getChoicesCountries())
    }
  }, [dispatch, countriesWasLoaded])

  useEffect(() => {
    if (userWasUpdated) {
      // reset redux state after successful user update
      dispatch(clearUpdateCurrentUserProfile())
    }
  }, [dispatch, userWasUpdated])

  return (
    <>
      <div className={classnames(commonClasses.heading, classes.editProfileHeading)}>
        <h3 className={commonClasses.title}>{t('User Profile')}</h3>
        <p>
          {t('You are registered with the email')} {email}.{' '}
          {t('If you would like to update your email, please message us using the chat function.')}
        </p>
      </div>
      <Form
        formik={formik}
        method="post"
        formName="edit_user_profile"
        successSubmit={userWasUpdated}
        errorSelector={updateCurrentUserErrorSelector}
        isLoadingSelector={updateCurrentUserLoadingSelector}
        submitText={t('Save')}
        formFooterClassName={classes.formFooter}
      >
        <div className={commonClasses.formRow}>
          <FieldLabelWrapper label={t('First name')} labelFor={FIRST_NAME}>
            <Field formik={formik} id={FIRST_NAME} name={FIRST_NAME} placeholder={t('First name')} />
          </FieldLabelWrapper>
          <FieldLabelWrapper label={t('Last name')} labelFor={LAST_NAME}>
            <Field formik={formik} id={LAST_NAME} name={LAST_NAME} placeholder={t('Last name')} />
          </FieldLabelWrapper>
        </div>
        <div className={commonClasses.formRow}>
          <FieldLabelWrapper label={t('Phone number')}>
            <PhoneInput
              className="field"
              name={PHONE_NUMBER}
              formik={formik}
              initialValue={formik.values[PHONE_NUMBER]}
              country={userCountryIso2}
            />
          </FieldLabelWrapper>
          <FieldLabelWrapper label={t('Default Country')}>
            <Field
              options={formattedCountries}
              formik={formik}
              id={COUNTRY}
              name={COUNTRY}
              placeholder={t('Default Country')}
            />
          </FieldLabelWrapper>
        </div>
      </Form>
    </>
  )
}

export default EditProfile
